/**
 * Buyer demand form (web) — Phase 5, buyer side.
 *
 * A signed-in, FICA-verified buyer posts a demand (a standing "I want to buy"
 * order): product + a quantity range (kg) + an offered price per kg + a needed-by
 * date. Coops match their batches against open demands (the coop-admin sees them
 * on /demands and the per-batch Matches panel).
 *
 * GATE: posting a demand requires buyer KYC. We read GET /buyers/me/eligibility
 * (getBuyerEligibility → { ficaStatus, canTransact }); when the buyer can't yet
 * transact we show a KYC-required prompt linking to /buyer-verification instead of
 * the form (the server is still the authority — it 403s buyer_kyc_required — but we
 * avoid a broken affordance). Mirrors the can-transact gate on
 * _app.buyer-verification.tsx.
 *
 * SUBMIT: useMarketControllerCreateDemand (the generated POST /market/demands
 * mutation). The DTO carries quantities as numeric kg STRINGS and the price as a
 * micro-ZARP (1e-6 ZARP) integer STRING — the buyer enters whole ZARP and we
 * convert with the inverse of microToZarp (×1_000_000, rounded). On success we
 * toast + redirect to /buyer-account.
 *
 * i18n keys live under createDemand.* in all four web locales. The nav item is
 * role-gated to `buyer` in _app.tsx (central wiring).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Money,
  Snackbar,
  Stack,
  StatusPill,
  Text,
  TextField,
} from '@fse/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { getBuyerEligibility } from '../api/buyers';
import { FseApiError } from '../api/client';
import { useMarketControllerCreateDemand } from '../api/generated/market/market';
import type { BuyerDemandDto } from '../api/generated/models';
import {
  getDemandResponses,
  listMyDemands,
  microToZarp,
  type DemandResponse,
} from '../api/market';

const MY_DEMANDS_QUERY_KEY = ['my-demands'];

const MICRO_PER_ZAR = 1_000_000;
const PRODUCT_MAX = 60; // matches CreateDemandDto @maxLength 60

const ELIGIBILITY_QUERY_KEY = ['buyer-eligibility'];

/** "12.50" whole-ZARP/kg → micro-ZARP integer STRING (inverse of microToZarp). */
function zarPerKgToMicroString(input: string): string | null {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n * MICRO_PER_ZAR));
}

/** A positive numeric kg value (carried as a string on the wire). */
function isPositiveQty(input: string): boolean {
  const n = Number(input);
  return Number.isFinite(n) && n > 0;
}

/** Today as YYYY-MM-DD (local) — the min for the needed-by date input. */
function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function CreateDemandScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Buyer KYC gate. Best effort: while pending we show a spinner; a hard error
  // (other than the eligibility object) blocks the form behind a retry.
  const eligibilityQuery = useQuery({
    queryKey: ELIGIBILITY_QUERY_KEY,
    queryFn: ({ signal }) => getBuyerEligibility(signal),
    retry: false,
  });
  const canTransact = eligibilityQuery.data?.canTransact ?? false;

  // The buyer's own demands + the seller responses on each (closes the loop:
  // post a demand → a matching seller responds → the buyer reads it here). Only
  // fetched once the buyer can transact (the list endpoint is buyer-scoped).
  const myDemandsQuery = useQuery({
    queryKey: MY_DEMANDS_QUERY_KEY,
    queryFn: ({ signal }) => listMyDemands(signal),
    enabled: canTransact,
  });

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('createDemand.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('createDemand.subtitle')}
          </Text>
        </Box>
      </Box>

      {eligibilityQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : eligibilityQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => eligibilityQuery.refetch()}>
              {t('createDemand.retry')}
            </Button>
          }
        >
          {t('createDemand.loadError', {
            error:
              eligibilityQuery.error instanceof FseApiError
                ? eligibilityQuery.error.message
                : String(eligibilityQuery.error),
          })}
        </Alert>
      ) : !canTransact ? (
        // KYC-required prompt — the buyer must verify before posting a demand.
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Alert severity="info">{t('createDemand.kycRequired.body')}</Alert>
              <Box>
                <Link to="/buyer-verification" style={{ textDecoration: 'none' }}>
                  <Button variant="contained">{t('createDemand.kycRequired.action')}</Button>
                </Link>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          <DemandForm
            onCreated={() =>
              // Stay on the page; refetch so the new demand appears in "My demands".
              void queryClient.invalidateQueries({ queryKey: MY_DEMANDS_QUERY_KEY })
            }
            t={t}
          />
          <MyDemandsSection
            demands={myDemandsQuery.data?.data ?? []}
            isPending={myDemandsQuery.isPending}
            isError={myDemandsQuery.isError}
            onRetry={() => myDemandsQuery.refetch()}
            t={t}
          />
        </>
      )}
    </Stack>
  );
}

interface MyDemandsSectionProps {
  demands: BuyerDemandDto[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  t: TFunction;
}

/** "My demands" — the buyer's posted demands, each expandable to its responses. */
function MyDemandsSection({ demands, isPending, isError, onRetry, t }: MyDemandsSectionProps) {
  return (
    <Box>
      <Text variant="title-large" as="h3">
        {t('createDemand.mine.title')}
      </Text>
      <Box sx={{ mt: 1.5 }}>
        {isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                {t('createDemand.retry')}
              </Button>
            }
          >
            {t('createDemand.mine.loadError')}
          </Alert>
        ) : demands.length === 0 ? (
          <Text variant="body-medium" color="text.secondary">
            {t('createDemand.mine.empty')}
          </Text>
        ) : (
          <Stack spacing={2}>
            {demands.map((d) => (
              <MyDemandCard key={d.id} demand={d} t={t} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// Map a demand status onto a StatusPill variant (the kit's semantic set is
// overdue|due|upcoming|active|sold). open = in progress (active), matched =
// closed positively (sold), expired/cancelled = no longer actionable (upcoming
// grey / overdue alert).
const STATUS_PILL_VARIANT: Record<string, 'active' | 'sold' | 'upcoming' | 'overdue'> = {
  open: 'active',
  matched: 'sold',
  expired: 'upcoming',
  cancelled: 'overdue',
};

/** One of the buyer's demands. Expands on demand to load + show its responses. */
function MyDemandCard({ demand, t }: { demand: BuyerDemandDto; t: TFunction }) {
  const [open, setOpen] = useState(false);

  // Only fetch the responses once the buyer expands the card (lazy per-demand).
  const responsesQuery = useQuery({
    queryKey: ['demand-responses', demand.id],
    queryFn: ({ signal }) => getDemandResponses(demand.id, signal),
    enabled: open,
  });

  const pillVariant = STATUS_PILL_VARIANT[demand.status] ?? 'upcoming';

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-medium" as="span">
              {demand.product}
            </Text>
            <StatusPill variant={pillVariant}>
              {t(`createDemand.status.${demand.status}`)}
            </StatusPill>
          </Stack>
        }
        subheader={
          <Text variant="label-medium" color="text.secondary">
            {demand.qtyKgMin}-{demand.qtyKgMax} kg
          </Text>
        }
      />
      <CardContent>
        <Button variant="text" size="small" onClick={() => setOpen((v) => !v)}>
          {open ? t('createDemand.mine.hideResponses') : t('createDemand.mine.viewResponses')}
        </Button>

        {open ? (
          responsesQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : responsesQuery.isError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t('createDemand.mine.responsesError')}
            </Alert>
          ) : (responsesQuery.data?.data.length ?? 0) === 0 ? (
            <Box sx={{ mt: 1 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('createDemand.mine.noResponses')}
              </Text>
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {responsesQuery.data!.data.map((r) => (
                <ResponseRow key={r.id} response={r} t={t} />
              ))}
            </Stack>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

/** A single seller response on the buyer's demand. */
function ResponseRow({ response, t }: { response: DemandResponse; t: TFunction }) {
  return (
    <Box sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
      <Text variant="title-small" as="div">
        {response.responderName ?? t('createDemand.mine.unknownSeller')}
      </Text>
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {response.qtyKg ? (
          <Text variant="label-medium" color="text.secondary">
            {t('createDemand.mine.canSupply', { qty: response.qtyKg })}
          </Text>
        ) : null}
        {response.priceZarpPerKgMicro ? (
          <Money
            amount={microToZarp(response.priceZarpPerKgMicro)}
            unit={t('createDemand.mine.perKg')}
            size="sm"
          />
        ) : null}
      </Stack>
      {response.note ? (
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium">{response.note}</Text>
        </Box>
      ) : null}
    </Box>
  );
}

interface DemandFormProps {
  onCreated: () => void;
  t: TFunction;
}

function DemandForm({ onCreated, t }: DemandFormProps) {
  const [product, setProduct] = useState('');
  const [qtyMin, setQtyMin] = useState('');
  const [qtyMax, setQtyMax] = useState('');
  const [price, setPrice] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const minIso = todayIso();

  // Field-level validity (used to drive inline error text + the submit gate).
  const productValid = product.trim().length > 0 && product.trim().length <= PRODUCT_MAX;
  const minValid = isPositiveQty(qtyMin);
  const maxValid = isPositiveQty(qtyMax);
  // Range coherence: max must be ≥ min when both are present.
  const rangeValid = minValid && maxValid && Number(qtyMax) >= Number(qtyMin);
  const priceMicro = zarPerKgToMicroString(price);
  const priceValid = priceMicro != null;
  const neededByValid = neededBy.length > 0 && neededBy >= minIso;

  const formValid = productValid && rangeValid && priceValid && neededByValid;

  const createMutation = useMarketControllerCreateDemand({
    mutation: {
      onSuccess: () => {
        setToastOpen(true);
        // Give the toast a beat, then hand back to the parent to redirect.
        window.setTimeout(onCreated, 900);
      },
    },
  });

  const submitError = useMemo(() => {
    if (!createMutation.isError) return null;
    return createMutation.error instanceof FseApiError
      ? createMutation.error.message
      : String(createMutation.error);
  }, [createMutation.isError, createMutation.error]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formValid || priceMicro == null) return;
    createMutation.mutate({
      data: {
        product: product.trim(),
        qtyKgMin: String(Number(qtyMin)),
        qtyKgMax: String(Number(qtyMax)),
        priceZarpPerKgMicro: priceMicro,
        neededBy,
      },
    });
  }

  return (
    <Card>
      <CardContent>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label={t('createDemand.fields.product')}
              value={product}
              onChange={(e) => setProduct(e.target.value.slice(0, PRODUCT_MAX))}
              error={product.length > 0 && !productValid}
              helperText={
                product.length > 0 && !productValid
                  ? t('createDemand.fields.productError')
                  : ' '
              }
              slotProps={{ htmlInput: { maxLength: PRODUCT_MAX } }}
              fullWidth
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                label={t('createDemand.fields.qtyMin')}
                value={qtyMin}
                onChange={(e) => setQtyMin(e.target.value)}
                inputMode="decimal"
                error={qtyMin.length > 0 && !minValid}
                helperText={
                  qtyMin.length > 0 && !minValid
                    ? t('createDemand.fields.qtyError')
                    : ' '
                }
                fullWidth
              />
              <TextField
                label={t('createDemand.fields.qtyMax')}
                value={qtyMax}
                onChange={(e) => setQtyMax(e.target.value)}
                inputMode="decimal"
                error={qtyMax.length > 0 && (!maxValid || (minValid && !rangeValid))}
                helperText={
                  qtyMax.length > 0 && !maxValid
                    ? t('createDemand.fields.qtyError')
                    : qtyMax.length > 0 && minValid && !rangeValid
                      ? t('createDemand.fields.rangeError')
                      : ' '
                }
                fullWidth
              />
            </Box>

            <TextField
              label={t('createDemand.fields.price')}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              error={price.length > 0 && !priceValid}
              helperText={
                price.length > 0 && !priceValid
                  ? t('createDemand.fields.priceError')
                  : t('createDemand.fields.priceHint')
              }
              fullWidth
            />

            <TextField
              label={t('createDemand.fields.neededBy')}
              type="date"
              value={neededBy}
              onChange={(e) => setNeededBy(e.target.value)}
              error={neededBy.length > 0 && !neededByValid}
              helperText={
                neededBy.length > 0 && !neededByValid
                  ? t('createDemand.fields.neededByError')
                  : ' '
              }
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: minIso } }}
              fullWidth
            />

            {submitError ? <Alert severity="error">{submitError}</Alert> : null}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!formValid || createMutation.isPending}
                startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
              >
                {createMutation.isPending
                  ? t('createDemand.submitting')
                  : t('createDemand.submit')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          {t('createDemand.toast.created')}
        </Alert>
      </Snackbar>
    </Card>
  );
}
